import { IDLE_TIMEOUT_MS } from './thresholds';

/**
 * Engaged time: time with the tab visible and the reader active. It is the
 * only definition of `dwell_ms`, for analytics and for the reader's own
 * stats alike.
 *
 * - The reader is active for `IDLE_TIMEOUT_MS` after each input (scroll,
 *   pointer, key). After that the clock pauses until the next input.
 * - Audio playing keeps the clock running through idleness: a reader
 *   listening with the article open is engaged without touching anything.
 * - A hidden tab always stops it, audio or not. Listening in the background
 *   is measured by the audio events, not counted as reading.
 *
 * No timers: the clock is settled whenever something changes or the total
 * is read, so it costs nothing while it runs and is exact under test.
 */
export interface EngagedClock {
	/** Starts (or restarts from zero) counting at `now`. */
	start: (now?: number) => void;
	/** The reader did something. */
	activity: (now?: number) => void;
	setVisible: (visible: boolean, now?: number) => void;
	setAudioPlaying: (playing: boolean, now?: number) => void;
	/** Engaged milliseconds since `start`, as a whole number. */
	elapsed: (now?: number) => number;
	/** Stops counting and returns the total. */
	stop: (now?: number) => number;
}

export function createEngagedClock({
	idleMs = IDLE_TIMEOUT_MS,
	visible: initiallyVisible = true,
	now: clock = () => Date.now(),
}: {
	idleMs?: number;
	visible?: boolean;
	now?: () => number;
} = {}): EngagedClock {
	let running = false;
	let visible = initiallyVisible;
	let audio = false;
	let total = 0;
	/** Everything before this instant is already in `total`. */
	let settledAt = 0;
	let lastActivityAt = 0;

	const settle = (now: number) => {
		if (running && visible) {
			const engagedUntil = audio ? now : Math.min(now, lastActivityAt + idleMs);
			if (engagedUntil > settledAt) {
				total += engagedUntil - settledAt;
			}
		}
		settledAt = Math.max(settledAt, now);
	};

	return {
		start: (now = clock()) => {
			running = true;
			total = 0;
			settledAt = now;
			lastActivityAt = now;
		},
		activity: (now = clock()) => {
			settle(now);
			lastActivityAt = now;
		},
		setVisible: (next, now = clock()) => {
			settle(now);
			// Coming back to the tab is the reader looking at it again.
			if (next && !visible) {
				lastActivityAt = now;
			}
			visible = next;
		},
		setAudioPlaying: (next, now = clock()) => {
			settle(now);
			// When audio stops, the idle countdown starts from that moment.
			if (!next && audio) {
				lastActivityAt = now;
			}
			audio = next;
		},
		elapsed: (now = clock()) => {
			settle(now);
			return Math.round(total);
		},
		stop: (now = clock()) => {
			settle(now);
			running = false;
			return Math.round(total);
		},
	};
}

// Whether article audio is playing, as the player reports it. Module-level
// because the player and the reader are different parts of the app and the
// clock is created fresh for every reading.
let audioPlaying = false;
const audioListeners = new Set<(playing: boolean) => void>();

/** Called by the audio player. Playing audio keeps every engaged clock running. */
export function setAudioPlaying(playing: boolean): void {
	if (audioPlaying === playing) {
		return;
	}
	audioPlaying = playing;
	audioListeners.forEach((listener) => listener(playing));
}

const ACTIVITY_EVENTS = [
	'scroll',
	'pointerdown',
	'pointermove',
	'keydown',
	'wheel',
	'touchstart',
] as const;

/**
 * Feeds a clock from the page: input anywhere in the document, the tab's
 * visibility, and whether article audio is playing. Returns a function that removes the listeners.
 */
export function attachEngagedClock(clock: EngagedClock): () => void {
	const onActivity = () => clock.activity();
	const onVisibility = () =>
		clock.setVisible(document.visibilityState === 'visible');

	clock.setVisible(document.visibilityState === 'visible');
	clock.setAudioPlaying(audioPlaying);
	const onAudio = (playing: boolean) => clock.setAudioPlaying(playing);
	audioListeners.add(onAudio);
	for (const type of ACTIVITY_EVENTS) {
		// Capture: scroll does not bubble, and the reader scrolls inner containers.
		document.addEventListener(type, onActivity, { passive: true, capture: true });
	}
	document.addEventListener('visibilitychange', onVisibility);

	return () => {
		for (const type of ACTIVITY_EVENTS) {
			document.removeEventListener(type, onActivity, { capture: true });
		}
		document.removeEventListener('visibilitychange', onVisibility);
		audioListeners.delete(onAudio);
	};
}
